import { useState, type FormEvent } from 'react';
import { SectionHeading } from './common';

const FORM_URL = 'https://readdy.ai/api/form/dagm281tto0ceqqnhvdg';

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐字段（机器人会填入），命中则走与正常提交一致的“成功”反馈
    const honeypot = String(formData.get('phone_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') payload.append(key, String(value));
    });

    setStatus('sending');
    setFormError('');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.code || responseText || '';
      if (
        !res.ok ||
        (parsed && parsed.code && parsed.code !== 'OK') ||
        serverMsg.toLowerCase().includes('spam')
      ) {
        setStatus('error');
        setFormError(
          serverMsg && !serverMsg.toLowerCase().includes('spam')
            ? serverMsg
            : '提交未成功，请稍后再试，或检查填写内容是否有误。',
        );
        return;
      }
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络似乎开小差了，请稍后再试一次。');
    }
  }

  return (
    <section id="contact" className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
      <SectionHeading
        kicker="爆料投稿"
        title="有瓜一起吃，有真相一起求"
        desc="如果你有关于校园热点的线索、完整的截图存证或某件事的最新进展，欢迎投给我们。我们只求证、不扩散，并对投稿内容做信息脱敏处理。"
      />

      <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="rounded-xl border border-stone-200 bg-white p-6">
            <h3 className="flex items-center gap-2 text-base font-bold text-stone-900">
              <i className="ri-message-2-line text-red-600" /> 投稿前请了解
            </h3>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-stone-600">
              <li className="flex gap-2.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-teal-600" />
                优先接收有<strong className="text-stone-800">官方来源 / 完整记录</strong>支撑的线索。
              </li>
              <li className="flex gap-2.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-teal-600" />
                涉及真实个人的内容，请先隐去姓名等敏感信息，我们不会扩散伤害性素材。
              </li>
              <li className="flex gap-2.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-teal-600" />
                留下联系方式便于我们与你核对细节，若不方便提供请留空。
              </li>
            </ul>
            <div className="mt-6 rounded-lg bg-stone-100 p-4 text-xs leading-relaxed text-stone-500">
              收到线索后我们会在 1-2 个工作日内跟进核验。若你更希望私下讨论，可在描述中注明，我们会按你的意愿处理。
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            data-readdy-form="校园吃瓜线索投稿表单"
            onSubmit={handleSubmit}
            className="rounded-xl border border-stone-200 bg-white p-6 md:p-8"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="type" className="mb-1.5 block text-sm font-medium text-stone-700">
                  线索类型
                </label>
                <select
                  id="type"
                  name="type"
                  required
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-200"
                >
                  <option value="">请选择线索类型</option>
                  <option value="文字爆料">文字爆料</option>
                  <option value="截图存证">截图存证</option>
                  <option value="官方通报">官方通报 / 公开记录</option>
                  <option value="完整经过">事件完整经过整理</option>
                  <option value="后续进展">某事件的后续进展</option>
                </select>
              </div>
              <div>
                <label htmlFor="nickname" className="mb-1.5 block text-sm font-medium text-stone-700">
                  你的称呼（可选）
                </label>
                <input
                  id="nickname"
                  name="nickname"
                  type="text"
                  placeholder="怎么称呼你"
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none placeholder:text-stone-400 focus:border-red-400 focus:ring-2 focus:ring-red-200"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-stone-700">
                联系邮箱（可选，用于回访核对）
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                className="w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none placeholder:text-stone-400 focus:border-red-400 focus:ring-2 focus:ring-red-200"
              />
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-stone-700">
                线索描述 <span className="text-stone-400">（请尽量写清时间、人物关系与你想求证的点）</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="例如：某学院奖学金名单事件——在 X 平台看到流传截图，我已保存原始链接，想求证名单是否官方发布……"
                className="w-full resize-none rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none placeholder:text-stone-400 focus:border-red-400 focus:ring-2 focus:ring-red-200"
              />
              <p className="mt-1 text-right text-xs text-stone-400">最多 500 字</p>
            </div>

            {/* 蜜罐：对用户不可见 */}
            <input
              type="text"
              name="phone_alt"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />

            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row">
              <button
                type="submit"
                disabled={status === 'sending'}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-red-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {status === 'sending' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> 提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-base" /> 提交线索
                  </>
                )}
              </button>
              <p className="text-xs text-stone-400">提交即代表你同意我们对信息进行核实与脱敏处理。</p>
            </div>

            {status === 'success' && (
              <p className="mt-4 rounded-lg bg-teal-50 px-4 py-3 text-sm font-medium text-teal-700">
                <i className="ri-checkbox-circle-fill mr-1.5" />
                感谢你的投稿，我们已经收到线索，将尽快跟进核验，感谢一起吃瓜求证～
              </p>
            )}
            {status === 'error' && (
              <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                <i className="ri-error-warning-fill mr-1.5" />
                {formError || '提交未成功，请稍后再试。'}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}